import { Button, DatePicker, Form } from 'antd'
import { ComponentProps } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import useSearchParams from '@/hooks/useSearchParams'
import { useAddNewScore } from '@/queries/measures/useAddNewScore'
import FormAntd from '@/ui/form/antd/FormAntd'
import cn from '@/utils/tailwind/cn'

import { AddNewScoreModel, useAddNewScoreForm } from './useAddNewScoreForm'

type Props = Omit<
  ComponentProps<typeof FormAntd.Root<AddNewScoreModel>>,
  'children'
>

export const AddNewScoreForm = ({ className, ...props }: Props) => {
  const { clientId = '' } = useParams<{ clientId?: string }>()
  const searchParams = useSearchParams()

  const formId = searchParams.get('formId') || ''
  const questionId = searchParams.get('question') || ''

  const { form, rule } = useAddNewScoreForm()
  const { addNewScore, isLoading } = useAddNewScore({
    questionId,
    formId,
    clientId,
  })

  const onFormSubmit = async (data: AddNewScoreModel) => {
    try {
      await addNewScore({
        data: {
          answer_value: data.score || '',
          answer_date: data?.date?.format('YYYY-MM-DD'),
        },
        questionId,
        formId,
        clientId,
      })
      toast.success('Score added successfully')
      form.resetFields()
    } catch (error) {
      toast.error('Error while add a score')
    }
  }

  return (
    <FormAntd.Root<AddNewScoreModel>
      className={cn('justify-end', className)}
      form={form}
      layout="vertical"
      onFinish={onFormSubmit}
      validateTrigger={['onBlur']}
      {...props}
    >
      <div className="mb-2 flex w-full gap-4">
        <Form.Item<AddNewScoreModel>
          className="w-full [&_label]:!text-sm [&_label]:font-medium"
          label="Score"
          name="score"
          rules={[rule]}
        >
          <FormAntd.Input allowClear placeholder="Enter score" type="number" />
        </Form.Item>
        <Form.Item<AddNewScoreModel>
          className="w-full [&_label]:!text-sm [&_label]:font-medium"
          label="Date"
          name="date"
          rules={[rule]}
        >
          <DatePicker
            className="w-full"
            format="MMMM DD, YYYY"
            placeholder="Select a date"
          />
        </Form.Item>
      </div>
      <Button
        disabled={isLoading}
        htmlType="submit"
        loading={isLoading}
        type="primary"
      >
        Save Score
      </Button>
    </FormAntd.Root>
  )
}
